import { useState, useEffect } from 'react'
import { Glasses, Smartphone, X, AlertCircle, Camera } from 'lucide-react'
import type { XRSessionState } from '@/types/xr'

interface ExtendedXRState extends XRSessionState {
  arModeAvailable?: 'webxr' | 'device-orientation' | 'none'
  vrModeAvailable?: boolean
  isIOS?: boolean
}

interface XRControlsProps {
  onEnterVR: () => void
  onEnterAR: () => void
  onExitXR: () => void
  xrState: ExtendedXRState
  className?: string
}

export function XRControls({
  onEnterVR,
  onEnterAR,
  onExitXR,
  xrState,
  className = ''
}: XRControlsProps) {
  const [vrSupported, setVRSupported] = useState(false)
  const [arSupported, setARSupported] = useState(false)
  const [checking, setChecking] = useState(true)

  useEffect(() => {
    const checkSupport = async () => {
      if (!navigator.xr) {
        setChecking(false)
        return
      }

      try {
        const [vr, ar] = await Promise.all([
          navigator.xr.isSessionSupported('immersive-vr'),
          navigator.xr.isSessionSupported('immersive-ar')
        ])
        setVRSupported(vr)
        setARSupported(ar)
      } catch (error) {
        console.error('Failed to check XR support:', error)
      } finally {
        setChecking(false)
      }
    }

    checkSupport()
  }, [])

  // Don't show controls if XR is not supported at all
  if (!navigator.xr || (!vrSupported && !arSupported && !checking)) {
    return null
  }

  // Show exit button if XR is active
  if (xrState.isActive) {
    return (
      <div className={`flex items-center gap-2 ${className}`}>
        <button
          onClick={onExitXR}
          className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
        >
          <X size={16} />
          Exit {xrState.sessionType === 'immersive-ar' ? 'AR' : 'VR'}
        </button>
        {xrState.error && (
          <div className="flex items-center gap-1 text-red-500 text-sm">
            <AlertCircle size={14} />
            {xrState.error}
          </div>
        )}
      </div>
    )
  }

  // Show enter buttons based on available modes
  return (
    <div className={`flex items-center gap-2 ${className}`}>
      {/* VR Button - only show if WebXR VR is supported */}
      {(vrSupported || xrState.vrModeAvailable) && (
        <button
          onClick={onEnterVR}
          disabled={checking}
          className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          title="View in Virtual Reality"
        >
          <Glasses size={16} />
          Enter VR
        </button>
      )}
      
      {/* AR Button - show different text based on mode */}
      {(arSupported || xrState.arModeAvailable === 'device-orientation') && (
        <button
          onClick={onEnterAR}
          disabled={checking}
          className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          title={xrState.arModeAvailable === 'webxr' ? 'View in Augmented Reality' : 'Camera AR Mode'}
        >
          {xrState.arModeAvailable === 'webxr' ? <Smartphone size={16} /> : <Camera size={16} />}
          {xrState.arModeAvailable === 'webxr' ? 'View in AR' : 'Camera AR'}
        </button>
      )}

      {checking && (
        <div className="text-sm text-gray-500">
          Checking XR support...
        </div>
      )}

      {xrState.error && (
        <div className="flex items-center gap-1 text-red-500 text-sm">
          <AlertCircle size={14} />
          {xrState.error}
        </div>
      )}
    </div>
  )
}

// Simplified XR button for minimal UI
export function XRButton({
  onEnterXR,
  xrSupported,
  sessionType = 'vr',
  className = ''
}: {
  onEnterXR: () => void
  xrSupported: boolean
  sessionType?: 'vr' | 'ar'
  className?: string
}) {
  if (!xrSupported) return null

  return (
    <button
      onClick={onEnterXR}
      className={`p-2 bg-gray-800/80 text-white rounded-lg hover:bg-gray-700/80 transition-colors backdrop-blur-sm ${className}`}
      title={sessionType === 'ar' ? 'View in AR' : 'Enter VR'}
    >
      {sessionType === 'ar' ? <Smartphone size={20} /> : <Glasses size={20} />}
    </button>
  )
}